import axios from 'axios'
import log from '@/utils/useLogger'

const GEOID_BASE_URL = 'https://v38rg7n7w4.execute-api.eu-west-2.amazonaws.com/Prod'

/** Single geoid height response from GET /geoid-height?lat=&lon= */
export interface GeoidHeightResponse {
  latitude: number
  longitude: number
  geoid_height: number
  unit: string
  model?: string
  model_description?: string
}

/** One result in the batch response */
export interface GeoidBatchResult {
  latitude: number
  longitude: number
  geoid_height: number
  unit: string
}

/** Batch geoid height response from POST /geoid-height */
export interface GeoidBatchResponse {
  count: number
  model: string
  model_description: string
  results: GeoidBatchResult[]
}

/** Coordinate for batch request */
export interface GeoidCoordinate {
  lat: number
  lon: number
}

// LRU Cache for geoid heights (same pattern as useMapFunctions terrain cache)
class LRUCache<K, V> {
  private cache: Map<K, V>
  private maxSize: number

  constructor(maxSize: number) {
    this.cache = new Map()
    this.maxSize = maxSize
  }

  get(key: K): V | undefined {
    if (!this.cache.has(key)) return undefined
    const value = this.cache.get(key)!
    this.cache.delete(key)
    this.cache.set(key, value)
    return value
  }

  set(key: K, value: V): void {
    if (this.cache.has(key)) {
      this.cache.delete(key)
    } else if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value
      if (firstKey !== undefined) this.cache.delete(firstKey)
    }
    this.cache.set(key, value)
  }

  has(key: K): boolean {
    return this.cache.has(key)
  }
}

const geoidHeightCache = new LRUCache<string, number>(1000)
const CACHE_PRECISION = 7

function cacheKey(lat: number, lon: number): string {
  return `${Number(lon).toFixed(CACHE_PRECISION)},${Number(lat).toFixed(CACHE_PRECISION)}`
}

/**
 * Composable for fetching geoid height (EGM2008) from the external API.
 * Uses an LRU cache keyed by lat/lon (same pattern as useMapFunctions terrain cache).
 * Single point: GET with query params. Batch: POST with coordinates; only uncached
 * points are requested, then results are merged and cached.
 */
export function useGeoidHeight() {
  /**
   * Get geoid height for a single location.
   * Uses cache; on cache miss calls GET /geoid-height?lat=&lon=
   */
  const getGeoidHeight = async (
    latitude: number,
    longitude: number,
  ): Promise<number> => {
    const key = cacheKey(latitude, longitude)
    if (geoidHeightCache.has(key)) {
      return geoidHeightCache.get(key)!
    }

    try {
      const { data } = await axios.get<GeoidHeightResponse>(
        `${GEOID_BASE_URL}/geoid-height`,
        { params: { lat: latitude, lon: longitude } },
      )
      const height = data.geoid_height
      geoidHeightCache.set(key, height)
      return height
    } catch (error) {
      log.error('Geoid height fetch failed:', error)
      return 0
    }
  }

  /**
   * Batch get geoid heights. Uses cache for known points and POST /geoid-height
   * only for uncached coordinates; results are merged and cached.
   */
  const getBatchGeoidHeights = async (
    coordinates: GeoidCoordinate[],
  ): Promise<number[]> => {
    const results: number[] = new Array(coordinates.length)
    const uncachedIndices: number[] = []
    const uncachedCoords: GeoidCoordinate[] = []

    coordinates.forEach((coord, index) => {
      const key = cacheKey(coord.lat, coord.lon)
      if (geoidHeightCache.has(key)) {
        results[index] = geoidHeightCache.get(key)!
      } else {
        uncachedIndices.push(index)
        uncachedCoords.push(coord)
      }
    })

    if (uncachedCoords.length === 0) return results

    try {
      const { data } = await axios.post<GeoidBatchResponse>(
        `${GEOID_BASE_URL}/geoid-height`,
        { coordinates: uncachedCoords },
      )

      data.results.forEach((result, i) => {
        const originalIndex = uncachedIndices[i]
        const coord = coordinates[originalIndex]
        const key = cacheKey(coord.lat, coord.lon)
        geoidHeightCache.set(key, result.geoid_height)
        results[originalIndex] = result.geoid_height
      })

      return results
    } catch (error) {
      log.error('Batch geoid height fetch failed:', error)
      uncachedIndices.forEach((index) => (results[index] = 0))
      return results
    }
  }

  return {
    getGeoidHeight,
    getBatchGeoidHeights,
  }
}
